import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroupDirective, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DisplayMode, SelectBox } from 'cbc-shared-lib';
import { Subject } from 'rxjs';
import { CodeConversionMstDto, Sgke903aResponseCompanyAreaInfo, Sgke903aResponseInitialhyojiInfo } from 'sg-api';

@Component({
  selector: 'app-sgke160d-search-condition',
  templateUrl: './sgke160d-search-condition.component.html',
  styleUrl: './sgke160d-search-condition.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class Sgke160dSearchConditionComponent implements OnInit, OnDestroy {

  /** モード */
  @Input() mode: string;
  /** コード変換マスタマップ */
  @Input() codeConversionMstMap: Map<string, Array<CodeConversionMstDto>> = null;
  /** 会社地区情報 */
  @Input() cmpnyAreaList: Array<Sgke903aResponseCompanyAreaInfo>;
  /** 初期表示用会社地区情報 */
  @Input() cmpnyAreaInit: Sgke903aResponseInitialhyojiInfo;
  /** 業務日付 */
  @Input() businessDate: Date = null;

  /** クリアボタン押下イベント */
  @Output() onClickClearEmitter = new EventEmitter();

  /** 入金締めステータス_入金締め未済 */
  private static RECEIVEMONEYJIME_STATUSCODE_UNCOMPLETED = '1';
  /** 入金締めステータス_入金締め承認依頼 */
  private static RECEIVEMONEYJIME_STATUSCODE_APPROVAL_REQUEST = '2';
  /** 入金締めステータス_入金締め済 */
  private static RECEIVEMONEYJIME_STATUSCODE_COMPLETED = '3';

  /** 購読を停止するためのSubject */
  private onDestroy$ = new Subject();
  /** 親フォームグループ情報 */
  private parentForm: UntypedFormGroup;

  /** モード区分_登録 */
  public MODE_CREATE = DisplayMode.create;
  /** モード区分_承認 */
  public MODE_APPROVE = DisplayMode.approve;

  /** 検索条件フォームグループ */
  searchConditionForm: UntypedFormGroup;

  /** 会社リスト */
  companyList: SelectBox[] = [];
  /** 地区リスト */
  areaList: SelectBox[] = [];
  /** 口座管理部署リスト */
  kouzaManageBushoList: SelectBox[] = [];

  /**
   * コンストラクタ
   * @param {FormGroupDirective} parent
   * @param {UntypedFormBuilder} fb
   * @memberof Sgke160dSearchConditionComponent
   */
  constructor(
    private parent: FormGroupDirective,
    private fb: UntypedFormBuilder,
  ) {
  }

  /**
   * 入金締めステータスコードを取得
   * @returns {string}
   * @memberof Sgke160dSearchConditionComponent
   */
  get receivemoneyjimeStatuscode(): string {
    return this.mode == this.MODE_APPROVE ? Sgke160dSearchConditionComponent.RECEIVEMONEYJIME_STATUSCODE_APPROVAL_REQUEST : Sgke160dSearchConditionComponent.RECEIVEMONEYJIME_STATUSCODE_UNCOMPLETED;
  }

  /**
   * 初期処理
   * @returns {void}
   * @memberof Sgke160dSearchConditionComponent
   */
  ngOnInit(): void {
    // 親フォームグループ取得
    this.parentForm = this.parent.form;

    // セレクターに値をセットする
    this.companyList.push(...this.getSelector('companycode', 'companycodeAname', this.cmpnyAreaList));

    // フォーム設定
    this.createForm();

    // 入金地区/口座管理部署リストを設定
    this.onChangeCompany(false);

  }

  /**
   * 購読解除
   * @returns {void}
   * @memberof Sgke160dSearchConditionComponent
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.parentForm.clearValidators();
    this.parentForm.updateValueAndValidity();
    this.parentForm.removeControl('cndt');
  }

  /**
   * コントロール取得
   * @param {string} ctrlName
   * @returns {UntypedFormControl}
   * @memberof Sgke160dSearchConditionComponent
   */
  getControl(ctrlName: string): UntypedFormControl {
    return this.searchConditionForm.get(ctrlName) as UntypedFormControl;
  }

  /**
   * 会社変更時処理
   * @param {boolean} [isNotInitialDisplay=true] 
   * @returns {void}
   * @memberof Sgke160dSearchConditionComponent
   */
  onChangeCompany(isNotInitialDisplay: boolean = true): void {
    // 会社コード
    const companycode = this.getControl('companycode').value;
    // 入金地区
    const chikuCtl = this.getControl('rcvmnyTantoAreacode') as UntypedFormControl;

    if (isNotInitialDisplay) {
      // 入金地区を未選択状態に設定
      chikuCtl?.setValue(null);
    }

    // 入金地区リスト初期化
    this.areaList = [];
    if (companycode) {
      const newAreaList: Array<Sgke903aResponseCompanyAreaInfo> = this.cmpnyAreaList
        .filter(list => list.companycode == companycode);
      this.areaList.push(...this.getSelector('areacode', 'areacodeAname', newAreaList));

    } else {
      this.areaList.push(...this.getSelector('areacode', 'areacodeAname', this.cmpnyAreaList));
    }

    this.onChangeChiku();
  }

  /**
   * 入金地区変更時処理
   * @param {string | null} areacode
   * @returns {void}
   * @memberof Sgke160dSearchConditionComponent
   */
  onChangeChiku(): void {
    // 入金地区
    const areacode = this.getControl('rcvmnyTantoAreacode').value;

    // 口座管理部署を未選択状態に設定
    const kouzaManageBushoCtl = this.getControl('kouzaManageBushocode') as UntypedFormControl;
    kouzaManageBushoCtl?.setValue(null);

    const companycode = this.getControl('companycode').value;

    // 口座管理部署リスト設定
    // 口座管理部署リスト初期化
    this.kouzaManageBushoList = [];
    const conditions: ((list: Sgke903aResponseCompanyAreaInfo) => boolean)[] = [];
    if (companycode) {
      // フィルター条件を追加
      conditions.push(list => list.companycode == companycode);
    }
    if (areacode) {
      // フィルター条件を追加
      conditions.push(list => list.areacode == areacode);
    }
    const newKouzaManageBushoList = this.cmpnyAreaList
      .filter(list => conditions.every(condition => condition(list)));
    this.kouzaManageBushoList.push(...this.getSelector('kouzaManageBushocode', 'kouzamanagebushoCodeAname', newKouzaManageBushoList));

  }

  /**
   * 入金締めステータス変更時処理
   * @returns {void}
   * @memberof Sgke160dSearchConditionComponent
   */
  onChangeStatus(): void {
    // 入金締めステータス
    const receivemoneyjimeStatuscode = this.getControl('receivemoneyjimeStatuscode').value;

    // 入金締め年月日
    const dateCtl = this.searchConditionForm.get('rcvmnyClsingYearmonthdate') as UntypedFormControl;

    if (receivemoneyjimeStatuscode == Sgke160dSearchConditionComponent.RECEIVEMONEYJIME_STATUSCODE_UNCOMPLETED) {
      // 入金締め年月日非活性化
      dateCtl?.disable();
      // 入金締め年月日初期化
      dateCtl.setValue(null);

    } else {
      // 入金締め年月日活性化
      dateCtl?.enable();
      // 入金締め年月日設定
      let setDate:Date = dateCtl.value;
      if(setDate == null){
        setDate = this.businessDate;
      }
      dateCtl.setValue(setDate);
    }

  }

  /**
   * クリアボタン押下時処理
   * @returns {void}
   * @memberof Sgke160dSearchConditionComponent
   */
  onClickClear(): void {
    // 入力内容をクリアする
    this.searchConditionForm?.reset();
    this.onChangeCompany(false);
    this.onChangeStatus();
    this.onClickClearEmitter.emit();
  }

  /**
   * フォーム作成
   * @private
   * @returns {void}
   * @memberof Sgke160dSearchConditionComponent
   */
  private createForm(): void {
    this.searchConditionForm = this.fb.group({
      // 会社
      companycode: this.fb.nonNullable.control({ value: this.cmpnyAreaInit.companycode, disabled: false }),
      // 入金地区
      rcvmnyTantoAreacode: this.fb.nonNullable.control({ value: this.cmpnyAreaInit.areacode, disabled: false }),
      // 口座管理部署
      kouzaManageBushocode: this.fb.nonNullable.control({ value: null, disabled: false }, Validators.required),
      // 入金締めステータス
      receivemoneyjimeStatuscode: this.fb.nonNullable.control({ value: this.receivemoneyjimeStatuscode, disabled: false }, Validators.required),
      // 入金締め年月日
      rcvmnyClsingYearmonthdate: this.fb.nonNullable.control({
        value: this.mode == this.MODE_APPROVE ? this.businessDate : null,
        disabled: this.mode == this.MODE_APPROVE ? false : true
      },
        Validators.required
      ),
      // 入金形態
      rcvMethodDiv: this.fb.nonNullable.control({ value: null, disabled: false }),
    });

    this.parentForm.addControl("cndt", this.searchConditionForm);

  }

  /**
   * SGKE903Aレスポンスからセレクターにオプションをセット
   * @param {string} value
   * @param {string} viewValue
   * @return {SelectBox[]}
   * @memberof Sgke160dSearchConditionComponent
   */
  private getSelector(value: string, viewValue: string, companyAreaInfoList: Array<Sgke903aResponseCompanyAreaInfo>): SelectBox[] {

    // SGKE903Aレスポンスのコードとコード値をまとめてリスト化
    let retSelectBoxList: SelectBox[] = [];
    companyAreaInfoList.map(data => {
      const select: SelectBox = { value: data[value], viewValue: data[viewValue] };
      retSelectBoxList.push(select);
    });

    // 重複を削除しソート
    const uniqueSortedArray = Array.from(
      new Map(retSelectBoxList.map(item => [item.value, item])).values()
    ).sort((a, b) => a.value > b.value ? 1 : -1);

    return uniqueSortedArray;
  }

}
